// src/lib/breathing.ts
// Конфигурация 4 дыхательных техник с научным обоснованием

export type BreathingTechniqueKey = 'box' | '4_7_8' | 'coherent' | 'physiological_sigh';

export type PhaseType = 'inhale' | 'hold' | 'exhale' | 'short_inhale';

export interface BreathingPhase {
  type: PhaseType;
  duration: number;        // секунды
  label: string;           // отображаемый текст
}

export interface BreathingTechnique {
  key: BreathingTechniqueKey;
  name: string;
  subtitle: string;        // ритм типа "4-4-4-4"
  rhythmShort: string;     // компактное отображение для круга
  purpose: string;         // зачем делать
  icon: string;            // tabler icon name
  color: string;           // основной цвет (hex)
  colorRgba: string;       // основной цвет в rgba для box-shadow
  gradientFrom: string;    // тёмный край фона карточки
  gradientTo: string;      // светлый край фона карточки
  phases: BreathingPhase[];
  recommendedCycles: number; // сколько циклов по умолчанию
  cycleDuration: number;     // продолжительность одного цикла в сек
  whenToUse: string;         // когда применять
  science: string;           // научное обоснование (краткое)
}

export const BREATHING_TECHNIQUES: Record<BreathingTechniqueKey, BreathingTechnique> = {
  box: {
    key: 'box',
    name: 'Бокс-дыхание',
    subtitle: '4-4-4-4',
    rhythmShort: '4·4·4·4',
    purpose: 'Фокус и контроль',
    icon: 'square',
    color: '#60b4eb',
    colorRgba: 'rgba(96,180,235,0.3)',
    gradientFrom: '#0a1f2e',
    gradientTo: '#0f2a3e',
    phases: [
      { type: 'inhale', duration: 4, label: 'Вдох' },
      { type: 'hold', duration: 4, label: 'Задержи' },
      { type: 'exhale', duration: 4, label: 'Выдох' },
      { type: 'hold', duration: 4, label: 'Задержи' },
    ],
    recommendedCycles: 8,
    cycleDuration: 16,
    whenToUse: 'Дедлайн, публичное выступление, нужно быстро вернуть контроль',
    science: 'Задержки повышают CO₂ в крови, что расширяет сосуды мозга и активирует парасимпатическую нервную систему через блуждающий нерв. Базовый протокол регуляции стресса армии США (Navy SEALs) и реаниматологов. Доказано: ВСР растёт почти мгновенно, тревожность и негативный аффект снижаются (Scientific Reports, 2022, мета-анализ 26 RCT).',
  },

  '4_7_8': {
    key: '4_7_8',
    name: 'Дыхание 4-7-8',
    subtitle: '4-7-8',
    rhythmShort: '4·7·8',
    purpose: 'Сон и тревога',
    icon: 'moon',
    color: '#b482eb',
    colorRgba: 'rgba(180,130,235,0.3)',
    gradientFrom: '#1a0f2e',
    gradientTo: '#2a1640',
    phases: [
      { type: 'inhale', duration: 4, label: 'Вдох носом' },
      { type: 'hold', duration: 7, label: 'Задержи' },
      { type: 'exhale', duration: 8, label: 'Выдох ртом' },
    ],
    recommendedCycles: 4,
    cycleDuration: 19,
    whenToUse: 'Не можешь уснуть, мысли крутятся, фоновая тревога',
    science: 'Выдох вдвое длиннее вдоха — мозг получает сигнал безопасности через блуждающий нерв, пульс замедляется. Разработан доктором Эндрю Вейлом на основе пранаямы. Исследование 2025 года (15 RCT, PRISMA) подтвердило снижение тревоги и улучшение качества сна — индекс Pittsburgh Sleep Quality Index снизился с 13.3 до 4.9.',
  },

  coherent: {
    key: 'coherent',
    name: 'Когерентное',
    subtitle: '5-5',
    rhythmShort: '5·5',
    purpose: 'Баланс нервной системы',
    icon: 'heart-rate-monitor',
    color: '#60dca5',
    colorRgba: 'rgba(96,220,165,0.3)',
    gradientFrom: '#0a2218',
    gradientTo: '#0f3024',
    phases: [
      { type: 'inhale', duration: 5, label: 'Вдох' },
      { type: 'exhale', duration: 5, label: 'Выдох' },
    ],
    recommendedCycles: 12,  // 12 циклов = ~2 минуты
    cycleDuration: 10,
    whenToUse: 'Ежедневная практика 10-15 минут для снижения базовой тревожности',
    science: '6 циклов в минуту — резонансная частота, при которой дыхание, давление и сердечный ритм входят в синхрон. Профессор Пол Лерер (Rutgers University) доказал: это даёт наибольший рост ВСР среди всех нефармакологических методов. Не быстрое средство — это тренажёр для нервной системы. Эффект накапливается при регулярной практике.',
  },

  physiological_sigh: {
    key: 'physiological_sigh',
    name: 'Физ. вздох',
    subtitle: '2 вдоха + выдох',
    rhythmShort: '2+8',
    purpose: 'Мгновенный сброс стресса',
    icon: 'bolt',
    color: '#ffaa64',
    colorRgba: 'rgba(255,170,100,0.3)',
    gradientFrom: '#2e1a0a',
    gradientTo: '#3e210f',
    phases: [
      { type: 'inhale', duration: 2, label: 'Вдох носом' },
      { type: 'short_inhale', duration: 1, label: 'Ещё вдох' },
      { type: 'exhale', duration: 5, label: 'Долгий выдох' },
    ],
    recommendedCycles: 5,
    cycleDuration: 8,
    whenToUse: 'Острый стресс, злость, паника — 1-3 цикла и система перезагружена',
    science: 'При стрессе альвеолы лёгких слипаются → растёт CO₂ → паника усиливается. Второй до-вдох раскрывает слипшиеся альвеолы, длинный выдох выводит CO₂. Исследование Стэнфорда 2023 года (Balban et al., Cell Reports Medicine, 108 участников, RCT) показало: 5 минут в день улучшают настроение эффективнее медитации. Один цикл — 30 секунд.',
  },
};

export const BREATHING_LIST: BreathingTechnique[] = [
  BREATHING_TECHNIQUES.box,
  BREATHING_TECHNIQUES['4_7_8'],
  BREATHING_TECHNIQUES.coherent,
  BREATHING_TECHNIQUES.physiological_sigh,
];

/**
 * Полная длительность практики в секундах
 */
export function getTotalDuration(technique: BreathingTechnique, cycles?: number): number {
  const c = cycles ?? technique.recommendedCycles;
  return technique.cycleDuration * c;
}

/**
 * Форматирует длительность в "M:SS"
 */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
